import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  CasesSendReportV1CaseDetailsApplicationContainerStaffUserCurrentDocument,
  Entity,
  SendReportCaseV1,
  SendReportCaseV1CaseDetailsApplicationReview,
  SendReportCaseV1UpdateInput,
  SendReportV1CaseDetailsApplicationContainerEntitiesDocument,
  SendReportV1CaseDetailsApplicationContainerSendReportCaseV1Document,
  SendReportV1CaseDetailsApplicationContainerSendReportCaseV1UpdateDocument,
  SendReportV1CaseDetailsApplicationContainerVerifiedCredentialVerificationCaseSummariesByApplicantDocument,
  SendReportV1CaseDetailsApplicationContainerVerifiedCredentialVerificationCaseSummariesByApplicantFieldsFragment
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../../../../../shared/utils/queryOptions';
import { ApplicationFormValueProps, CaseDetailsApplication } from './case-details-application';

interface CaseDetailsApplicationContainerProps {
  caseId: string;
  isCaseOrApplicant: string;
}

export const CaseDetailsApplicationContainer: React.FC<CaseDetailsApplicationContainerProps> = (props) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const { data, loading, error } = useQuery(SendReportV1CaseDetailsApplicationContainerSendReportCaseV1Document, {
    variables: {
      sendReportCaseV1Id: props.caseId
    },
    onCompleted: (data) => {
      getApplicantCredentialCases({
        variables: {
          applicantId: data?.sendReportCaseV1?.applicant?.id
        }
      });
    }
  });

  const [getApplicantCredentialCases, { data: credCasesByApplicantData, loading: credCasesByApplicantLoading, error: credCasesByApplicantError }] = useLazyQuery(
    SendReportV1CaseDetailsApplicationContainerVerifiedCredentialVerificationCaseSummariesByApplicantDocument,
    {
      context: {
        headers: {
          'cache-enabled': 'true'
        }
      }
    }
  );
  const {
    data: staffData,
    loading: staffLoading,
    error: staffError
  } = useQuery(CasesSendReportV1CaseDetailsApplicationContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const [updateSendReportCase, { loading: updateLoading }] = useMutation(SendReportV1CaseDetailsApplicationContainerSendReportCaseV1UpdateDocument, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: SendReportV1CaseDetailsApplicationContainerSendReportCaseV1Document,
        variables: {
          sendReportCaseV1Id: props.caseId
        }
      }
    ]
  });

  const { data: entities, loading: entitiesLoading, error: entitiesError } = useQuery(SendReportV1CaseDetailsApplicationContainerEntitiesDocument);

  const onSave = async (values: ApplicationFormValueProps) => {
    const inputData: SendReportCaseV1UpdateInput = {
      id: props.caseId,
      caseName: values.caseName,
      archiveEligibleAt: values.archiveEligibleAt,
      expirationEligibleAt: values.expirationEligibleAt,
      caseDetails: {
        application: {
          destination: values.destination,
          credential: values.credential
        }
      }
    };

    if (data?.sendReportCaseV1?.state === 'draft') {
      inputData.purgeEligibleAt = values.purgeEligibleAt;
    }

    return updateSendReportCase({
      variables: {
        input: inputData
      }
    });
  };

  return (
    <ComponentQueryLoader
      error={error || entitiesError || credCasesByApplicantError || staffError}
      loading={loading || entitiesLoading || credCasesByApplicantLoading || staffLoading}
      hasData={data && entities && credCasesByApplicantData && staffData}
      hasDataComponent={
        <CaseDetailsApplication
          staffUserType={staffData?.staffUserCurrent?.role?.roleSubtype as string}
          purgeEligibleAt={data?.sendReportCaseV1?.purgeEligibleAt}
          archiveEligibleAt={data?.sendReportCaseV1?.archiveEligibleAt}
          expirationEligibleAt={data?.sendReportCaseV1?.expirationEligibleAt}
          data={data?.sendReportCaseV1 as SendReportCaseV1}
          entities={entities?.entities as Entity[]}
          caseId={data?.sendReportCaseV1?.id}
          onSave={onSave}
          isSavingApplication={updateLoading}
          credentialCases={
            credCasesByApplicantData?.verifiedCredentialVerificationCaseSummariesByApplicant as SendReportV1CaseDetailsApplicationContainerVerifiedCredentialVerificationCaseSummariesByApplicantFieldsFragment[]
          }
          applicationReview={data?.sendReportCaseV1?.caseDetails?.applicationReview as SendReportCaseV1CaseDetailsApplicationReview}
          caseState={data?.sendReportCaseV1?.state as string}
          isCurrentUserAssigned={data?.sendReportCaseV1?.isCurrentUserAssigned as boolean}
          isCaseOrApplicant={props.isCaseOrApplicant}
        />
      }
    />
  );
};
